import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { AppConstants } from '../commons/app-constants';

// Completes the Capacitor social-login flow started by LoginComponent.onSocialLoginClick
// (Browser.open, since Google/Facebook block OAuth inside an embedded WebView). Catches the
// app being re-opened via the lumberlinq://oauth/callback deep link that
// OAuth2LoginSuccessHandler redirects the system browser tab to once the provider round-trip
// finishes, and exchanges the one-time code for a real session — from inside the app's own
// WebView, via a plain HttpClient POST, so the resulting Set-Cookie headers land in the
// WebView's cookie jar rather than the Custom Tab's (a different cookie store entirely).
//
// init() is called once from AppComponent's constructor (eager singleton — same pattern as
// SyncEngineService/StatusBarService there), and is a no-op on web/non-native.
@Injectable({ providedIn: 'root' })
export class MobileOAuthCallbackService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);

  private static readonly DEEP_LINK_PREFIX = 'lumberlinq://oauth/callback';

  // Confirmed 2026-08-26 via backend logs: 'appUrlOpen' fires TWICE for a single real deep-link
  // open on some devices (a known Capacitor/OS-level quirk, not something this app's own code
  // asks for) — the SAME one-time code reaches handleUrlOpen() twice, milliseconds apart. The
  // first exchange succeeds and consumes the code; the second is correctly rejected by the
  // backend as already-used, but if the app happened to show THAT failure to the user, they'd
  // see "social login failed" immediately after a login that actually just succeeded. Tracking
  // the last code processed makes a duplicate event a silent no-op instead — this guards
  // correctly regardless of which underlying mechanism causes the duplicate.
  private lastProcessedCode: string | null = null;

  init(): void {
    if (!Capacitor.isNativePlatform()) return;
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      // Capacitor's native→JS bridge callback can land outside Angular's zone, which would
      // silently break change detection for the HTTP subscription below.
      this.ngZone.run(() => this.processDeepLink(event?.url || '', true));
    });
  }

  // Public on purpose: also called directly by ConfirmLogoutDevicesComponent on native platforms
  // (see that component's own comment) instead of routing the same lumberlinq:// URL through
  // window.location.replace(). That approach relied on iOS/WKWebView correctly handing an
  // unrecognized custom scheme back to this same app via the OS — confirmed 2026-08-26 this is
  // unreliable on iOS specifically (Android's WebView is far more permissive about this kind of
  // handoff, which is why this code path was never caught before iOS testing started). Since
  // we're already running inside this exact app instance either way, there's no real need to
  // round-trip through the OS at all — parsing the URL and calling this directly is both more
  // reliable and skips a pointless app-switch flicker.
  // closeBrowser: only true for the real appUrlOpen path (an actual Custom Tab is genuinely open
  // and needs dismissing) — ConfirmLogoutDevicesComponent never opened one, so it passes false.
  processDeepLink(raw: string, closeBrowser: boolean): void {
    if (!raw.startsWith(MobileOAuthCallbackService.DEEP_LINK_PREFIX)) return;

    if (closeBrowser) {
      Browser.close().catch(() => { /* already closed — not fatal */ });
    }

    // CONFIRMED ROOT CAUSE (2026-08-26), found by comparing the exact code value the backend
    // issued against the exact value it later received: iOS appends an empty URL fragment ('#')
    // to the deep link before handing it back to the app (a known custom-URL-scheme quirk, not
    // something this app's own code does) — e.g. "...code=abc123" arrives as "...code=abc123#".
    // Plain URLSearchParams has no idea '#' means "fragment, stop parsing here" unless it's given
    // a real URL object instead of a bare string, so that trailing '#' silently became part of
    // the code value itself, and the server correctly rejected it as not matching what it issued.
    // Fixed by stripping everything from '#' onward before parsing, exactly like a real URL
    // parser would.
    const queryIndex = raw.indexOf('?');
    const rawQuery = queryIndex >= 0 ? raw.slice(queryIndex + 1) : '';
    const query = rawQuery.split('#')[0];
    const params = new URLSearchParams(query);
    const code = params.get('code');
    const error = params.get('error');

    // TEMPORARY DEBUG (2026-08-26) — downgraded from a blocking alert() to a plain log line.
    // The earlier alert() paused JS execution for several seconds waiting for "OK" to be
    // tapped, which delayed the actual POST below by that same amount — meaning a test done
    // with the alert in place couldn't distinguish "the fix worked" from "the delay happened to
    // dodge a real race condition". This still lets the code value be compared against the
    // backend's own log line, but with none of that artificial delay, so the next test reflects
    // genuine, undelayed timing.
    if (code) {
      this.logger.debug('[MobileOAuth] Extracted code from deep link', code);
    }

    if (error) {
      this.logger.warn('[MobileOAuth] Provider returned error', error);
      // Route through /login's authError query param instead of a toast — LoginComponent
      // already renders this as the same inline <p-message> pill web uses for the identical
      // error codes (and flips to sign-up + prefills email for userNotRegistered /
      // socialNotRegistered), so mobile gets the exact same UX as web for free.
      this.navigateToLoginWithError(error);
      return;
    }
    if (!code) {
      this.logger.warn('[MobileOAuth] Deep link had neither code nor error', raw);
      return;
    }
    if (code === this.lastProcessedCode) {
      // Duplicate 'appUrlOpen' fire for the same real deep-link open — see this.lastProcessedCode's
      // comment above. Silently ignored: the first fire already handled this code either way.
      this.logger.debug('[MobileOAuth] Ignoring duplicate deep-link event for an already-processed code');
      return;
    }
    this.lastProcessedCode = code;

    this.http.post(`${AppConstants.API_URL}api/v1/auth/mobile/token-exchange`, { code })
      .subscribe({
        next: () => {
          this.logger.info('[MobileOAuth] Token exchange succeeded');
          // Full reload (not router.navigate) — app.component.ts's bootstrap/menu/profile
          // load is keyed off app startup and NavigationEnd, and this session is brand new
          // from the WebView's perspective (cookies just landed via Set-Cookie), so a real
          // reload is simpler and more reliable than trying to thread a "refresh everything"
          // signal through already-initialized singletons.
          window.location.href = '/';
        },
        error: (err) => {
          this.logger.error('[MobileOAuth] Token exchange failed', err);
          // No specific error code available here (the code itself was valid enough to reach
          // this deep link, the backend exchange failed — e.g. expired/already-used code) —
          // 'socialLoginFailed' matches web's own default/unclassified-error case.
          this.navigateToLoginWithError('socialLoginFailed');
        },
      });
  }

  // Full reload, not router.navigate — the app's own WebView never left /login while the
  // external Custom Tab was open (Browser.open doesn't navigate this WebView), so LoginComponent
  // is already mounted and its ngOnInit already ran. LoginComponent reads authError via a
  // one-time route.snapshot read in ngOnInit (not a queryParams subscription), so a same-route
  // router.navigate wouldn't re-trigger it under Angular's default route-reuse — the pill would
  // silently never show. A real reload guarantees a fresh ngOnInit, and matches both web's actual
  // HTTP-redirect behavior for this same authError flow and this service's own token-exchange
  // success handler just above, which reloads for the identical reason.
  private navigateToLoginWithError(authError: string): void {
    window.location.href = `/login?authError=${encodeURIComponent(authError)}`;
  }
}
